import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { StickyNote, Trash2, ChevronDown, Lock } from "lucide-react";

export default function InquiryNotes({ q, onChange }) {
  const notes = q.notes || [];
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const id = q.inquiry_id;

  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post(`/admin/inquiries/${id}/notes`, { text });
      setText("");
      toast.success("Note ajoutée");
      onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const remove = async (noteId) => {
    if (!window.confirm("Supprimer cette note ?")) return;
    try { await api.delete(`/admin/inquiries/${id}/notes/${noteId}`); toast.success("Note supprimée"); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-4 border-t border-gray-100 pt-3">
      <button data-testid={`inquiry-notes-toggle-${id}`} onClick={() => setOpen(!open)} className="flex items-center gap-2 text-sm text-[#1B2A4A] hover:text-[#C9A85A] transition-colors">
        <StickyNote className="w-4 h-4 text-[#E0C173]" strokeWidth={1.5} />
        Notes internes ({notes.length})
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid={`inquiry-notes-${id}`} className="mt-3 space-y-3">
          <p className="text-xs text-gray-500 flex items-center gap-1"><Lock className="w-3 h-3" /> Visibles uniquement par l'équipe PROAC, jamais par l'acheteur.</p>
          {notes.length === 0 && <p className="text-sm text-gray-500 italic">Aucune note pour le moment.</p>}
          <ol className="space-y-2 border-l-2 border-[#E0C173]/50 pl-4">
            {notes.map((n) => (
              <li key={n.note_id} data-testid={`inquiry-note-${n.note_id}`} className="bg-[#F8F6F0] p-3 group relative">
                <div className="text-xs text-gray-500 pr-8">{n.author_name} · {new Date(n.created_at).toLocaleString("fr-FR")}</div>
                <p className="text-sm text-[#1B2A4A] mt-1 whitespace-pre-line">{n.text}</p>
                <button data-testid={`inquiry-note-delete-${n.note_id}`} onClick={() => remove(n.note_id)} className="absolute top-2 right-2 text-gray-400 hover:text-red-600 transition-colors" aria-label="Supprimer la note">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ol>
          <Textarea data-testid={`inquiry-note-input-${id}`} value={text} onChange={(e) => setText(e.target.value)} placeholder="Ex. : Appel le 12/10, envoi du dossier complet, relance prévue la semaine prochaine…" className="rounded-none border-gray-300 text-sm" />
          <button data-testid={`inquiry-note-add-${id}`} onClick={add} disabled={saving || !text.trim()} className="text-sm px-4 py-1.5 bg-[#1B2A4A] text-white hover:bg-[#2a3d63] disabled:opacity-40 transition-colors">
            {saving ? "Ajout…" : "Ajouter la note"}
          </button>
        </div>
      )}
    </div>
  );
}
